import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { Store } from '@ngxs/store';
import { SignOutOnError } from '@platform-frontend/account';
import {
  AcademicRegistration,
  FlowStartupService,
  MSG_CONFIRM_ACADEMIC_EMAIL_CONFIRM_ID_EXPIRED,
  MSG_CONFIRM_ACADEMIC_EMAIL_CONFIRM_ID_NOT_MATCHING_EMAIL,
  MSG_CONFIRM_ACADEMIC_EMAIL_USER_NOT_KNOWN,
  MSG_SHOW_CONFIRMATION_PAGE_AFTER_CONFIRM_ACADEMIC_EMAIL_FAILED,
  PrepareFlow,
  SIGN_IN_FLOW_DEFAULT,
  SetFlow,
  UserType,
  getStartAcademicRegistrationPreferenceFromUserType
} from '@platform-frontend/checkout';
import { CheckoutLanguage } from '@platform-frontend/checkout-drop-in';
import { MARKETPLACE_ERROR_HANDLERS } from '@platform-frontend/marketplace';
import { PlannedError, ShowErrorNotification, ShowSuccessNotification, SimpleRouterState, handleAllErrors, handleErrors } from '@platform-frontend/util';
import { Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export const ConfirmAcademicEmailStartupGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const store = inject(Store);
  const flowStartupService = inject(FlowStartupService);

  if (store.selectSnapshot(SimpleRouterState.guardsActivated)) {
    const productId: string | null = route.queryParamMap.get('productId');
    const userType: string | null = route.queryParamMap.get('userType');
    const errorType: string | null = route.queryParamMap.get('errorType');
    const locale: CheckoutLanguage | null = route.queryParamMap.get('locale') as CheckoutLanguage | null;
    const metadataId: string | null = route.queryParamMap.get('metadataId');

    if (productId && userType) {
      const flow: AcademicRegistration = {
        type: 'ACADEMIC_REGISTRATION',
        startParams: {
          productIdentifier: { productId, environment: 'LIVE' },
          start: getStartAcademicRegistrationPreferenceFromUserType(userType as UserType),
          locale: locale,
          metadataId
        }
      };
      flowStartupService.getBaseStartupObservable(flow, () => showErrorOrAcademicConfirmation(errorType, store)).subscribe();
    }
  }

  return true;
};

const showErrorOrAcademicConfirmation: (errorType: string | null, store: Store) => Observable<unknown> = (errorType: string | null, store: Store) =>
  (errorType ? throwError(() => new PlannedError(errorType)) : of(null)).pipe(
    handleErrors(MARKETPLACE_ERROR_HANDLERS, {
      USER_NOT_KNOWN: new ShowErrorNotification(MSG_CONFIRM_ACADEMIC_EMAIL_USER_NOT_KNOWN),
      CONFIRM_ID_NOT_MATCHING_EMAIL: new ShowErrorNotification(MSG_CONFIRM_ACADEMIC_EMAIL_CONFIRM_ID_NOT_MATCHING_EMAIL),
      CONFIRM_ID_EXPIRED: new ShowErrorNotification(MSG_CONFIRM_ACADEMIC_EMAIL_CONFIRM_ID_EXPIRED)
      // SOLUTION_ID_NOT_MATCHING_CONFIRM_ID
      // USER_TYPE_NOT_MATCHING_CONFIRM_ID
    }),
    switchMap(() => prepareFlowForAcademicConfirmationPage(store))
  );

const prepareFlowForAcademicConfirmationPage: (store: Store) => Observable<unknown> = (store: Store) => {
  return store
    .dispatch(new PrepareFlow())
    .pipe(
      handleAllErrors([
        new SetFlow(SIGN_IN_FLOW_DEFAULT),
        new SignOutOnError(new ShowSuccessNotification(MSG_SHOW_CONFIRMATION_PAGE_AFTER_CONFIRM_ACADEMIC_EMAIL_FAILED))
      ])
    );
};
